import React, { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Container,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Stack,
  CircularProgress,
  Paper,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';

import AddIcon from '@mui/icons-material/PlaylistAdd';
import EditIcon from '@mui/icons-material/ModeEdit';
import UploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import PaidIcon from '@mui/icons-material/Paid';

import { useMutation, useLazyQuery } from '@apollo/client';
import PricelistImportDialog from './PricelistImportDialog';
import PricelistDeleteDialog from './PricelistDeleteDialog';
import PricelistEditModal from './PricelistEditModal';
import { getAdminSelectedCompanyId } from "../../../helpers/adminSelectors";

import { GET_PRICE_LISTS } from '../../../graphql/queries/CompanyAdminContext';
import { DELETE_PRICELIST } from '../../../graphql/mutations';

const Pricelists = () => {

  const intl = useIntl();
  const companyId = useSelector(getAdminSelectedCompanyId);

  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const [selectedPl, setSelectedPl] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false); // <-- edit modal state
  const [actionLoading, setActionLoading] = useState(false);

  //
  // LOAD PRICELISTS
  //
  const [loadPriceLists, { refetch }] = useLazyQuery(GET_PRICE_LISTS, {
    variables: { companyId },
    onCompleted: (d) => {
      const lists = d?.companyAdminContext?.pricelists?.getPriceLists || [];
      setPriceLists(lists);
      setLoading(false);
    },
    onError: (err) => {
      console.error(err);
      setErrorMsg(intl.formatMessage({ id: "admin.pricelists.loadingError" }));
      setLoading(false);
    },
  });

  useEffect(() => {
    if (companyId) {
      setLoading(true);
      loadPriceLists();
    }
  }, [companyId]);

  //
  // DELETE MUTATION
  //
  const [deletePriceList] = useMutation(DELETE_PRICELIST);

  const openDelete = (pl) => {
    setSelectedPl(pl);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedPl) return;
    setActionLoading(true);
    try {
      const { data } = await deletePriceList({
        variables: { articlePriceListId: selectedPl.articlePriceListId }
      });
      setActionLoading(false);
      setDeleteOpen(false);

      const result = data?.companyAdminContext?.pricelists?.deletePricelist;
      if (result?.success) {
        setPriceLists((prev) => prev.filter(p => p.articlePriceListId !== selectedPl.articlePriceListId));
      } else {
        setErrorMsg(intl.formatMessage({ id: "admin.pricelists.deleteError" }));
      }
    } catch (err) {
      console.error(err);
      setActionLoading(false);
      setDeleteOpen(false);
      setErrorMsg(intl.formatMessage({ id: "admin.pricelists.deleteError" }));
    }
  };

  //
  // IMPORT
  //
  const openImport = (pl) => {
    setSelectedPl(pl);
    setImportOpen(true);
  };

  //
  // EXPORT
  //
  const handleExport = (pl) => {
    const href = `/api/pricelists/export?articlePriceListId=${pl.articlePriceListId}&companyId=${companyId}`;
    window.location.href = href;
  };

  //
  // EDIT
  //
  const openEdit = (pl = null) => {
    setSelectedPl(pl);
    setEditOpen(true);
  };

  const handleSaved = () => {
    refetch();
    setEditOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openEdit(null)} // open modal for new pricelist
          >
            <FormattedMessage id="admin.pricelists.add" />
          </Button>
        </Box>
      </Box>

      <Paper>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : priceLists.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <UploadIcon sx={{ fontSize: 48 }} />
              <Typography variant="h6"><FormattedMessage id="admin.pricelists.empty.title" /></Typography>
            <Typography sx={{ mb: 2 }}>
                <FormattedMessage id="admin.pricelists.empty.description" />
            </Typography>
              <Button variant="contained" onClick={() => openEdit(null)}><FormattedMessage id="admin.pricelists.add" /></Button>
          </Box>
        ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><FormattedMessage id="admin.pricelists.table.name" /></TableCell>
                    <TableCell><FormattedMessage id="admin.pricelists.table.description" /></TableCell>
                    <TableCell><FormattedMessage id="admin.pricelists.table.actions" /></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {priceLists.map((pl) => (
                    <TableRow key={pl.articlePriceListId}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {pl.name}
                          {pl.isCompanyInvoicePriceList && (
                            <Tooltip title={intl.formatMessage({id:"admin.pricelists.table.primary"})}>
                              <PaidIcon color="primary" fontSize="small" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{pl.description}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton onClick={() => openEdit(pl)}>
                            <EditIcon />
                          </IconButton>{/*}
                          <IconButton onClick={() => openImport(pl)}>
                            <UploadIcon />
                          </IconButton>
                          <IconButton onClick={() => handleExport(pl)}>
                            <DownloadIcon />
                          </IconButton>*/}
                          <IconButton onClick={() => openDelete(pl)}>
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
      </Paper>

      {/* IMPORT DIALOG */}
      <PricelistImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        companyId={companyId}
        articlePriceListId={selectedPl?.articlePriceListId}
        onImported={() => refetch()}
      />

      {/* DELETE DIALOG */}
      <PricelistDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        pricelistName={selectedPl?.name}
        onConfirm={confirmDelete}
        loading={actionLoading}
      />

      {/* EDIT MODAL */}
      <PricelistEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        articlePriceListId={selectedPl?.articlePriceListId}
        companyId={companyId}
        onSaved={handleSaved}
      />

      {/* ERROR SNACKBAR */}
      <Snackbar open={!!errorMsg} autoHideDuration={4000} onClose={() => setErrorMsg('')}>
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>
    </Container>
  );
};

export default Pricelists;
